import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import { VehicleStateEnum } from "@/lib/enum/vehicleState.enum";
import vehicleService from "@/services/mrfiktiv/services/vehicleService";
import {
  MrfiktivCreateLeasingContractDtoGen,
  MrfiktivCreateVehicleDocumentDtoGen,
  MrfiktivCreateVehicleDtoGen,
  MrfiktivLeasingContractGen,
  MrfiktivMileageGen,
  MrfiktivTimestampGen,
  MrfiktivUpdateLeasingContractDtoGen,
  MrfiktivUpdateVehicleDtoGen,
  MrfiktivUpdateVehicleRegistrationDtoGen,
  MrfiktivVehicleControllerCreateBatchPayloadGen,
  MrfiktivVehicleControllerGetAllParamsGen,
  MrfiktivVehicleDocumentSchemaGen,
  MrfiktivVehicleRegistrationViewModelGen,
  MrfiktivVehicleViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, getModule } from "vuex-module-decorators";
import { BasePagination, FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";
import { FleetAggregationModule } from "./fleet-aggregation.store";
import Vue from "vue";

export class VehicleRegistration implements MrfiktivVehicleRegistrationViewModelGen {}

/**
 * Ui class for vehicles
 */
export class Vehicle implements MrfiktivVehicleViewModelGen {
  /**
   * @inheritdoc
   */
  id: string;

  /**
   * @inheritdoc
   */
  partnerId: string;

  /**
   * @inheritdoc
   */
  numberplate: string;

  /**
   * @inheritdoc
   */
  identificationnumber?: string;

  /**
   * @inheritdoc
   */
  displayName?: string;

  /**
   * @inheritdoc
   */
  commissioningDate?: string;

  /**
   * @inheritdoc
   */
  state?: VehicleStateEnum;

  /**
   * @inheritdoc
   */
  available?: boolean;

  /**
   * @inheritdoc
   */
  tags?: string[];

  /**
   * @inheritdoc
   */
  registration?: MrfiktivVehicleRegistrationViewModelGen;

  /**
   * @inheritdoc
   */
  contracts?: MrfiktivLeasingContractGen[];

  /**
   * @inheritdoc
   */
  mileages?: MrfiktivMileageGen[];

  /**
   * @inheritdoc
   */
  currentDriver?: string;

  /**
   * @inheritdoc
   */
  drivers?: string[];

  /**
   * @inheritdoc
   */
  note?: string;

  /**
   * @inheritdoc
   */
  timestamp: MrfiktivTimestampGen;

  /**
   * @inheritdoc
   */
  documents?: MrfiktivVehicleDocumentSchemaGen[];

  constructor(vehicle: MrfiktivVehicleViewModelGen) {
    this.id = vehicle.id;
    this.partnerId = vehicle.partnerId;
    this.numberplate = vehicle.numberplate;
    this.identificationnumber = vehicle.identificationnumber;
    this.displayName = vehicle.displayName;
    this.commissioningDate = vehicle.commissioningDate;
    this.state = (vehicle.state as VehicleStateEnum) || undefined;
    this.available = vehicle.available;
    this.tags = vehicle.tags;
    this.registration = vehicle.registration || new VehicleRegistration();
    this.contracts = vehicle.contracts;
    this.mileages = vehicle.mileages;
    this.currentDriver = vehicle.currentDriver;
    this.drivers = vehicle.drivers;
    this.note = vehicle.note;
    this.timestamp = vehicle.timestamp;
    this.documents = vehicle.documents;
  }

  get registrationDate() {
    const registration = this.registration;
    if (
      !registration ||
      !registration.firstregistrationDay ||
      !registration.firstregistrationMonth ||
      !registration.firstregistrationYear
    ) {
      return "";
    }

    return `${registration.firstregistrationYear}-${registration.firstregistrationMonth?.padStart(
      2,
      "0"
    )}-${registration.firstregistrationDay?.padStart(2, "0")}`;
  }

  set registrationDate(registrationDate: string) {
    if (!registrationDate) {
      return;
    }

    if (registrationDate) {
      const [year, month, day] = registrationDate.split("-");

      if (!this.registration) {
        this.registration = new VehicleRegistration();
      }

      this.registration.firstregistrationDay = day;
      this.registration.firstregistrationMonth = month;
      this.registration.firstregistrationYear = year;
    }
  }
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "vehicle",
  store
})
export class VehicleStore extends BasePagination<
  MrfiktivVehicleViewModelGen,
  MrfiktivVehicleControllerGetAllParamsGen
> {
  protected _pageOrder: PageOrderEnum = PageOrderEnum.DESCENDING;
  protected _itemsPerPage = 500;
  protected _totalPages = 0;
  protected _paginationList: MrfiktivVehicleViewModelGen[] = [];
  protected _currentPage = 1;
  protected _totalItems = 0;
  protected _isLoadAll = true;
  protected _filterList: IPaginationFilterListElement[] = [
    { key: "partnerId", type: FilterTypes.OBJECT_ID },
    { key: "numberplate", type: FilterTypes.STRING },
    { key: "identificationnumber", type: FilterTypes.STRING },
    { key: "displayName", type: FilterTypes.STRING },
    { key: "commissioningDate", type: FilterTypes.STRING },
    { key: "state", type: FilterTypes.ENUM, config: { items: Object.values(VehicleStateEnum) } },
    { key: "available", type: FilterTypes.BOOLEAN },
    { key: "note", type: FilterTypes.STRING },
    { key: "currentDriver", type: FilterTypes.STRING },
    { key: "_id", type: FilterTypes.OBJECT_ID }
  ];
  private _idMap = new Map<string, MrfiktivVehicleViewModelGen>();
  private _vinMap = new Map<string, MrfiktivVehicleViewModelGen>();
  protected _documentMapConfig = { id: this._idMap, identificationnumber: this._vinMap };

  get stateMap() {
    return new Map<string, { color: string; label: string }>([
      [
        VehicleStateEnum.UPCOMING,
        { color: "rgb(224,224,224)", label: "views.fleet.FleetVehicleListView.state.upcoming" }
      ],
      [VehicleStateEnum.ACTIVE, { color: "#9EE09E", label: "views.fleet.FleetVehicleListView.state.active" }],
      [VehicleStateEnum.PARTIAL, { color: "#F9E79F", label: "views.fleet.FleetVehicleListView.state.partial" }],
      [VehicleStateEnum.INACTIVE, { color: "#FFADAD", label: "views.fleet.FleetVehicleListView.state.inactive" }],
      [
        VehicleStateEnum.DELETED,
        { color: "blue-grey darken-4", label: "views.fleet.FleetVehicleListView.state.deleted" }
      ]
    ]);
  }

  @Action
  protected async loadDocuments(query: MrfiktivVehicleControllerGetAllParamsGen): Promise<any> {
    const res = await vehicleService.getAll(query);

    Vue.$log.debug("Paged Vehicles. Parsing for fleet ", res.data?.length);
    FleetAggregationModule.parse(res.data ?? []);

    return res;
  }

  private _vehicle: MrfiktivVehicleViewModelGen | undefined = undefined;

  private _vehicleLookup: Map<string, MrfiktivVehicleViewModelGen> = new Map();

  get vehicle() {
    return this._vehicle;
  }

  get vehicles() {
    return this._paginationList;
  }

  /**
   * Gets all vehicles in a lookup table
   *
   */
  get vehicleLookup() {
    return this._vehicleLookup;
  }

  @Mutation
  mutateVehicle(vehicle: MrfiktivVehicleViewModelGen) {
    this._vehicle = vehicle;
  }

  @Mutation
  mutateVehicles(vehicles: MrfiktivVehicleViewModelGen[]) {
    this._vehicleLookup = new Map();

    vehicles.forEach(obj => this._vehicleLookup.set(obj.id, obj));

    this._paginationList = Array.from(this._vehicleLookup.values());
  }

  @Action
  async create(data: { partnerId: string; data: MrfiktivCreateVehicleDtoGen }) {
    const res = await vehicleService.create(data.partnerId, data.data);

    this.context.commit("mutateVehicle", res);
    FleetAggregationModule.parse([res]);
    this.paginationList.unshift(res);

    return res;
  }

  @Action
  async createBatch(data: { partnerId: string; data: MrfiktivVehicleControllerCreateBatchPayloadGen }) {
    const res = await vehicleService.createBatch(data.partnerId, data.data);

    this.context.commit("mutateVehicles", res);

    return res;
  }

  @Action
  async createContract(data: { partnerId: string; vehicleId: string; data: MrfiktivCreateLeasingContractDtoGen }) {
    const res = await vehicleService.createContract(data.partnerId, data.vehicleId, data.data);

    this.context.commit("mutateVehicle", res);

    (await FleetAggregationModule.getVehicleAggregation(data.vehicleId))
      ?.updateContracts(res.contracts)
      .parseContractDates();

    return res;
  }

  @Action
  async createDocument(data: { partnerId: string; vehicleId: string; data: MrfiktivCreateVehicleDocumentDtoGen }) {
    const res = await vehicleService.createDocument(data.partnerId, data.vehicleId, data.data);

    this.context.commit("mutateVehicle", res);

    return res;
  }

  @Action
  async delete(data: { partnerId: string; vehicleId: string }) {
    const res = await vehicleService.delete(data.partnerId, data.vehicleId);

    this.context.commit("mutateVehicle", res);
    FleetAggregationModule.removeAggregation(data.vehicleId);

    return res;
  }

  @Action
  async deleteContract(data: { partnerId: string; vehicleId: string; contractId: string }) {
    const res = await vehicleService.deleteContract(data.partnerId, data.vehicleId, data.contractId);

    this.context.commit("mutateVehicle", res);
    (await FleetAggregationModule.getVehicleAggregation(data.vehicleId))
      ?.updateContracts(res.contracts)
      .parseContractDates();
    return res;
  }

  @Action
  async deleteDocument(data: { partnerId: string; vehicleId: string; documentId: string }) {
    const res = await vehicleService.deleteDocument(data.partnerId, data.vehicleId, data.documentId);

    this.context.commit("mutateVehicle", res);

    return res;
  }

  @Action
  async getAll(data: { partnerId: string }) {
    await this.fetchAllFromBeginning({
      partnerId: data.partnerId
    });

    this.context.commit("mutateVehicles", this._paginationList);
  }

  @Action
  async getOne(data: { partnerId: string; vehicleId: string }) {
    const res = await vehicleService.getOne(data.partnerId, data.vehicleId);

    this.context.commit("mutateVehicle", res);

    return res;
  }

  @Action
  async update(data: { partnerId: string; vehicleId: string; data: MrfiktivUpdateVehicleDtoGen }) {
    const res = await vehicleService.update(data.partnerId, data.vehicleId, data.data);

    this.context.commit("mutateVehicle", res);

    (await FleetAggregationModule.getVehicleAggregation(data.vehicleId))
      ?.updateMileages(res.mileages)
      .parseMileageDates();

    return res;
  }

  @Action
  async updateContract(data: {
    partnerId: string;
    vehicleId: string;
    contractId: string;
    data: MrfiktivUpdateLeasingContractDtoGen;
  }) {
    const res = await vehicleService.updateContract(data.partnerId, data.vehicleId, data.contractId, data.data);

    this.context.commit("mutateVehicle", res);
    (await FleetAggregationModule.getVehicleAggregation(data.vehicleId))
      ?.updateContracts(res.contracts)
      .parseContractDates();

    return res;
  }

  @Action
  async updateDocument(data: {
    partnerId: string;
    vehicleId: string;
    documentId: string;
    data: MrfiktivCreateVehicleDocumentDtoGen;
  }) {
    const res = await vehicleService.updateDocument(data.partnerId, data.vehicleId, data.documentId, data.data);

    this.context.commit("mutateVehicle", res);

    return res;
  }

  @Action
  async updateRegistration(data: {
    partnerId: string;
    vehicleId: string;
    data: MrfiktivUpdateVehicleRegistrationDtoGen;
  }) {
    const res = await vehicleService.updateRegistration(data.partnerId, data.vehicleId, data.data);

    this.context.commit("mutateVehicle", res);
    (await FleetAggregationModule.getVehicleAggregation(data.vehicleId))?.updateRegistration(res.registration);

    return res;
  }
}

export const VehicleModule = getModule(VehicleStore);
